<template>
    <div class="top-table-filter-container">
        <!-- Десктопная версия -->
        <div class="green-filter d-none d-md-flex mb-2 align-items-center">
            <!-- Фильтр статусов -->
            <span v-for="(item, index) in greenFilterMenu" 
                  :key="index" 
                  class="mr-2"
                  :class="item.active ? 'green-link active' : 'green-link'" 
                  @click="changeActive(index)">
                {{ item.title }}
            </span>
            
            <!-- Фильтр по агентам (только для неагентов) -->
            <div class="ml-3 mt-1" v-if="!isAgent && canViewAllAgents">
                <div class="corrected-multiselect__tags small-tags" style="min-width: 200px; max-width: 250px;">
                    <multiselect
                        v-model="selectedAgents"
                        :options="agentOptions"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :show-labels="false"
                        label="text"
                        track-by="value"
                        placeholder="Фильтр по агентам"
                        @input="onAgentsFilterChange"
                    >
                        <span slot="noResult">Агенты не найдены</span>
                        <span slot="noOptions">Список агентов пуст</span>
                    </multiselect>
                </div>
            </div>
            
            <div class="ml-auto" v-if="isAgent">
                <b-button
                    variant="success"
                    size="sm"
                    @click="openStatisticsSidebar"
                >
                    <i class="bx bx-bar-chart"></i> Статистика
                </b-button>
            </div>

            <!-- Кнопка назначения клиентов -->
            <div class="ml-auto">
                <b-button
                    variant="success"
                    size="sm"
                    @click="openImportSidebar"
                    v-if="canAssignClients"
                    title="Импорт клиентов"
                >
                    <i class="bx bx-user-plus"></i>
                </b-button>
                <b-button
                    variant="success"
                    size="sm"
                    @click="openAssignModal"
                    v-if="canAssignClients"
                >
                    <i class="bx bx-user-plus"></i> Назначить клиентов
                </b-button>
            </div>
        </div>

        <!-- Мобильная версия -->
        <div class="d-flex d-md-none mobile-filter-container">
            <!-- Бургер-меню -->
            <button 
                class="burger-menu-btn"
                @click="toggleMobileMenu"
                :class="{ active: isMobileMenuOpen }"
            >
                <span class="burger-line"></span>
                <span class="burger-line"></span>
                <span class="burger-line"></span>
            </button>

            <!-- Выпадающее меню -->
            <div class="mobile-dropdown" :class="{ show: isMobileMenuOpen }">
                <div class="mobile-filter-section">
                    <h6 class="mobile-filter-title">Статус</h6>
                    <div class="mobile-filter-items">
                        <span 
                            v-for="(item, index) in greenFilterMenu"
                            :key="index"
                            :class="`mobile-filter-item ${item.active ? 'active' : ''}`"
                            @click="changeActive(index); closeMobileMenu()"
                        >
                            {{ item.title }}
                        </span>
                    </div>
                </div>

                <!-- Фильтр по агентам (мобильная версия) -->
                <div class="mobile-filter-section" v-if="!isAgent && canViewAllAgents">
                    <h6 class="mobile-filter-title">Агенты</h6>
                    <div class="mobile-filter-items" style="padding: 0 10px;">
                        <multiselect
                            v-model="selectedAgents"
                            :options="agentOptions"
                            :multiple="true"
                            :searchable="true"
                            :close-on-select="false"
                            :show-labels="false"
                            label="text"
                            track-by="value"
                            placeholder="Выберите агентов"
                            @input="onAgentsFilterChange"
                        >
                            <span slot="noResult">Агенты не найдены</span>
                            <span slot="noOptions">Список агентов пуст</span>
                        </multiselect>
                    </div>
                </div>

                <!-- Кнопки действий -->
                <div class="mobile-filter-section" v-if="isAgent || canAssignClients">
                    <h6 class="mobile-filter-title">Действия</h6>
                    <div class="mobile-filter-items">
                        <span 
                            v-if="isAgent"
                            class="mobile-filter-item"
                            @click="openStatisticsSidebar(); closeMobileMenu()"
                        >
                            <i class="bx bx-bar-chart"></i> Статистика
                        </span>
                        <span 
                            v-if="canAssignClients"
                            class="mobile-filter-item"
                            @click="openImportSidebar(); closeMobileMenu()"
                        >
                            <i class="bx bx-user-plus"></i> Импорт клиентов
                        </span>
                        <span 
                            v-if="canAssignClients"
                            class="mobile-filter-item"
                            @click="openAssignModal(); closeMobileMenu()"
                        >
                            <i class="bx bx-user-plus"></i> Назначить клиентов
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { API_AGENT_COMPANY } from '@/constants/api';

export default {
    props: {
        api: {type: String, required: true}, // Ссылка на API для работы с таблицей
    },
    data() {
        return {
            isMobileMenuOpen: false,
            greenFilterMenu: [
                {title: "Все", value: "", active: true},
                {title: "Необработанные", value: "new", active: false},
                {title: "Связались", value: "contacted", active: false},
                {title: "Заинтересованы", value: "interested", active: false},
                {title: "Не заинтересованы", value: "not_interested", active: false},
                {title: "Не дозвонились", value: "no_answer", active: false}
            ],
            selectedAgents: [],
            agentOptions: [],
            loadingAgents: false
        };
    },
    computed: {
        ...mapGetters("auth", ["currentUser"]),
        
        canAssignClients() {
            const userRoles = this.currentUser.role || []
            return userRoles.includes('administrator') || userRoles.includes('president')
        },

        isAgent() {
            return this.currentUser.role.includes('agent')
        },

        canViewAllAgents() {
            const userRoles = this.currentUser.role || []
            // Менеджеры, руководители и администраторы могут видеть всех агентов
            return userRoles.some(role => 
                role.includes('manager') || 
                role.includes('head-manager') || 
                role === 'administrator' || 
                role === 'president'
            )
        }
    },    
    mounted() {
        this.changeActive(this.$store.state.userLocalSettings.filterSettings.coldClientsTable || 0);
        if (this.canViewAllAgents && !this.isAgent) {
            this.loadAgentCompanies();
        }
    },
    methods: {
        changeActive(activeIndex) {
            let additionalParameter = '';

            // Сохраняем выбор в store
            this.$store.dispatch("userLocalSettings/SET_FILTER_SETTINGS", {
                name: 'coldClientsTable',
                value: activeIndex
            });

            // Обновляем активный статус в фильтре
            this.greenFilterMenu.forEach((item, index) => {
                item.active = activeIndex == index;
                
                if (activeIndex == index && item.value) {
                    additionalParameter += `&cooperation_status=${item.value}`;
                }
            });

            // Добавляем фильтр по агентам, если выбран
            if (this.selectedAgents && this.selectedAgents.length > 0) {
                const agentIds = this.selectedAgents.map(agent => agent.value).join(',');
                additionalParameter += `&agent_company_ids=${agentIds}`;
            }

            this.$emit('setFilteringApiParameter', additionalParameter);
        },

        async loadAgentCompanies() {
            try {
                this.loadingAgents = true;
                
                const response = await api.call('get', API_AGENT_COMPANY, {
                    params: {
                        per_page: 1000,
                        check_status: 'approved'
                    }
                });
                
                if (response.data && response.data.data) {
                    this.agentOptions = response.data.data.map(company => ({
                        value: company.id,
                        text: `${company.full_name}${company.inn ? ' / ' + company.inn : ''}${(company.region && company.region.fullname) ? ' (' + company.region.fullname + ')' : ''}`
                    }));
                }
            } catch (error) {
                console.error('Ошибка загрузки агентских компаний:', error);
            } finally {
                this.loadingAgents = false;
            }
        },

        onAgentsFilterChange() {
            // Пересчитываем параметры фильтрации при изменении выбора агентов
            const activeIndex = this.greenFilterMenu.findIndex(item => item.active);
            if (activeIndex >= 0) {
                this.changeActive(activeIndex);
            }
        },
        
        openAssignModal() {
            this.$emit('universalEmit', 'openAssignModal');
        },

        openStatisticsSidebar() {
            this.$emit('universalEmit', 'openStatisticsSidebar');
        },

        openImportSidebar() {
            this.$emit('universalEmit', 'openImportSidebar');
        },

        // Методы для мобильного меню
        toggleMobileMenu() {
            this.isMobileMenuOpen = !this.isMobileMenuOpen;
        },

        closeMobileMenu() {
            this.isMobileMenuOpen = false;
        }
    }
};
</script>
